import { BaseApiClient } from '../client'
import type { MerchantSettings, UpdateIntegrationsInput } from '../types'
import type { KlaviyoStatus, TinyStatus, TinyFormaEnvio, TinyConfigInput } from '@/lib/types'

export class SettingsApi extends BaseApiClient {
  async get() {
    return this.request<MerchantSettings>('/api/admin/settings')
  }

  async updateIntegrations(data: UpdateIntegrationsInput) {
    return this.request<{ success: true; message: string }>('/api/admin/settings/integrations', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async removeIntegration(integration: 'guru' | 'yampi' | 'klaviyo') {
    return this.request<{ success: true; message: string }>(
      `/api/admin/settings/integrations/${integration}`,
      {
        method: 'DELETE',
      }
    )
  }

  async regenerateApiKey() {
    return this.request<{ publicApiKey: string }>('/api/admin/settings/api-key', {
      method: 'POST',
    })
  }

  // Klaviyo Integration
  async getKlaviyoStatus() {
    return this.request<KlaviyoStatus>('/api/admin/settings/klaviyo')
  }

  async saveKlaviyoApiKey(apiKey: string) {
    return this.request<{ success: true; message: string }>('/api/admin/settings/klaviyo', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    })
  }

  async testKlaviyoConnection() {
    return this.request<{ success: boolean; error?: string; message?: string }>(
      '/api/admin/settings/klaviyo/test',
      {
        method: 'POST',
      }
    )
  }

  async removeKlaviyo() {
    return this.request<{ success: true; message: string }>('/api/admin/settings/klaviyo', {
      method: 'DELETE',
    })
  }

  async syncGuruTransactions(data: { merchantId: string; daysBack: number }) {
    return this.request<{ success: true; message: string }>('/api/admin/sync/guru-transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Import Klaviyo Profiles
  async importKlaviyoProfiles() {
    return this.request<{ success: boolean; message: string; jobId?: string }>(
      '/api/admin/settings/klaviyo/import',
      {
        method: 'POST',
      }
    )
  }

  async getKlaviyoImportStatus() {
    return this.request<{
      id: string
      status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' // Added cancelled
      totalProfiles: number | null // Added
      processedCount: number
      createdCount: number
      existingCount: number
      skippedCount: number
      failedCount: number
      currentPage: number
      lastProcessedEmail: string | null
      lastError: string | null
      startedAt: string
      completedAt: string | null
      errorMessage: string | null
      speed: number | null
      elapsedSeconds: number
    } | null>('/api/admin/settings/klaviyo/import/status')
  }

  async retryProfileCount(jobId: string) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/settings/klaviyo/import/${jobId}/count`,
      {
        method: 'POST',
      }
    )
  }

  async cancelKlaviyoImport(jobId: string) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/settings/klaviyo/import/${jobId}/cancel`,
      {
        method: 'POST',
      }
    )
  }

  // Tiny ERP Integration
  async getTinyStatus() {
    return this.request<TinyStatus>('/api/admin/settings/tiny')
  }

  async saveTinyConfig(data: TinyConfigInput) {
    return this.request<{ success: true; message: string }>('/api/admin/settings/tiny', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async testTinyConnection() {
    return this.request<{ success: boolean; error?: string; message?: string }>(
      '/api/admin/settings/tiny/test',
      {
        method: 'POST',
      }
    )
  }

  async removeTiny() {
    return this.request<{ success: true; message: string }>('/api/admin/settings/tiny', {
      method: 'DELETE',
    })
  }

  async getTinyShippingMethods() {
    return this.request<TinyFormaEnvio[]>('/api/admin/settings/tiny/shipping-methods')
  }

  // Discord Webhook Integration
  async getDiscordStatus() {
    return this.request<{ enabled: boolean; hasWebhookUrl: boolean }>('/api/admin/settings/discord')
  }

  async saveDiscordWebhookUrl(webhookUrl: string) {
    return this.request<{ success: true; message: string }>('/api/admin/settings/discord', {
      method: 'PUT',
      body: JSON.stringify({ webhookUrl }),
    })
  }

  async testDiscordNotification() {
    return this.request<{ success: boolean; error?: string; message?: string }>(
      '/api/admin/settings/discord/test',
      { method: 'POST' }
    )
  }

  async testDiscordReport() {
    return this.request<{ success: boolean; error?: string; message?: string }>(
      '/api/admin/settings/discord/test-report',
      { method: 'POST' }
    )
  }

  async removeDiscord() {
    return this.request<{ success: true; message: string }>('/api/admin/settings/discord', {
      method: 'DELETE',
    })
  }
}
